import Button from '@components/ui/ButtonV2/Button'

import FormControl from '@components/ui/inputs/FormControl'
import TextInput from '@components/ui/inputs/TextInput/TextInput'
import { zodResolver } from '@hookform/resolvers/zod'
import { getBirthdateLimit } from '@lib/generateBirthDateLimit'
import { CustomerQuery, CustomerUpdateMutation } from '@lib/graphql/graphql'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import signupNewsletter from '@lib/newsletter-signup'
import { selectCountry, selectLabels } from '@redux/globalSlice'
import { openModalView } from '@redux/modalSlice'
import { mapEdgesToItems } from '@utils/map'
import clsx from 'clsx'
import {
  CustomerUpdateFormValues,
  customerUpdateFormSchema,
  useCustomerUpdate,
} from 'features/account/api/customer-update'
import { useCustomer } from 'features/account/api/get-customer'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { HiPlus } from 'react-icons/hi'
import { useDispatch, useSelector } from 'react-redux'
import AccountTypeForm from '../AccountTypeForm/AccountTypeForm'
import { actionOtpSendToken } from '../M58ForgotPasswordForm/SetPasswordStep'
import Address from './Address/Address'
import s from './M61ProfileView.module.scss'
import { UserProfileConsentNewsletterToggle } from './NewsletterToggle'
import PrefrenceInputRender, {
  PreferenceInputType,
} from './PreferenceInputRender'

interface M61ProfileViewProps {
  customerInfo?: CustomerQuery
}

const addKeyIfNotPresent = (
  arr: Array<{ key: string; value: string }>,
  defaultValue: { key: string; value: string },
) => {
  if (!arr.some((pref) => pref.key == defaultValue.key)) {
    arr.push(defaultValue)
  }
  return arr
}

const M61ProfileView: FC<M61ProfileViewProps> = ({ customerInfo }) => {
  let { data } = useCustomer({})
  const router = useRouter()

  const birthdateLimit = getBirthdateLimit()

  if (!data) data = customerInfo

  const customer = data?.customer
  const customerPreferences = customer?.metadata ?? []
  const customerGroups = mapEdgesToItems(customer?.customerGroups)
  addKeyIfNotPresent(customerPreferences, { key: 'birthDate', value: '' })
  addKeyIfNotPresent(customerPreferences, { key: 'interests', value: '[]' })

  const methods = useForm<CustomerUpdateFormValues>({
    resolver: zodResolver(customerUpdateFormSchema),
    defaultValues: {
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      preferences: customerPreferences,
    },

    mode: 'onBlur',
  })

  const { watch } = methods

  const { mutateAsync, isPending } = useCustomerUpdate()

  const { customErrors, loading, addError, clearErrors } = useCustomErrors()

  const [loadingSpinner, setLoadingSpinner] = useState(false)
  const country = useSelector(selectCountry)
  const labels = useSelector(selectLabels)

  const onSubmit = async (formData: CustomerUpdateFormValues) => {
    setLoadingSpinner(true)

    try {
      const response = (await mutateAsync({
        data: formData,
      })) as CustomerUpdateMutation

      if (response.customerUpdate.customer) {
        clearErrors()

        if (formData.newsletterSignup.changed) {
          if (formData.newsletterSignup.value) {
            const isArchitectAndDesigner =
              !!preferences.find((x) => x.key == 'businessName') ||
              customerGroups?.some(
                (x) =>
                  x.id === 'cusgrp_01jjps1p5ye76te2w3msakj83b' ||
                  x.id === 'cusgrp_01k47a6axkeqrt710ybzmcbxqe',
              )
            const response = await signupNewsletter({
              email: customer?.email,
              list: isArchitectAndDesigner ? 'PROFESSIONALS' : 'CONSUMERS',
              country: country,
              language: router.query.language as string,
              getCountry: Cookies.get('homeCountry') ?? '',
            })

            if (response.ok) {
              methods.setValue('newsletterSignup', {
                value: true,
                changed: false,
              })
            }
          } else {
            const response = await fetch(
              '/api/account/newsletter-unsubscribe',
              {
                method: 'POST',
              },
            )

            const data = await response.json()

            if (data.success)
              methods.setValue('newsletterSignup', {
                value: false,
                changed: false,
              })
          }
        }
      }
    } catch (error) {
      console.error(error)

      addError({
        code: 'unknown',
        message: 'Something went wrong',
      })
    } finally {
      setTimeout(() => setLoadingSpinner(false), 1000)
    }
  }

  const formId = 'account-details-form'
  const dispatch = useDispatch()
  const preferences = watch('preferences')

  const preferenceInputs: { [key: string]: PreferenceInputType } = {
    birthDate: {
      inputName: 'Birth Date',
      inputType: 'date',
      min: birthdateLimit.maxDate,
      max: birthdateLimit.minDate,
      sortOrder: 1,
      footerSection: false,
      required: true,
    },
    interests: {
      inputName: 'Interests',
      inputType: 'interests',
      sortOrder: 3,
      footerSection: true,
      formId: formId,
    },
  }

  const indexedPreferences = preferences.map((preference, index) => ({
    preference,
    index,
  }))

  // if URL contains query parameter resetPasswordToken, open the reset password modal
  useEffect(() => {
    if (router.query.resetPasswordToken && typeof window !== 'undefined') {
      dispatch(
        openModalView({
          modalView: 'RESET_PASSWORD_MODAL',
          props: {
            data: {
              tokenString: router.query.resetPasswordToken,
            },
          },
        }),
      )
    }
  }, [router.query.resetPasswordToken])

  const addresses = mapEdgesToItems(customer?.addresses)

  const customerGroup = customerGroups.find(Boolean)

  return (
    <div className={s.root}>
      <div className={s.section}>
        <h2 className={s.headline}>Account Details</h2>

        {customErrors && customErrors.length > 0 && (
          <div className={s.errorContainer}>
            {customErrors.map((error) => (
              <div key={error.code} className={s.error}>
                {error.message}
              </div>
            ))}
          </div>
        )}

        <div className={clsx(s.formGroup, s.formGroup__column)}>
          <FormProvider {...methods}>
            <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
              <Controller
                control={methods.control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <FormControl
                    className={s.formInputWrapper}
                    required
                    layout="horizontal"
                  >
                    <FormControl.Label>First Name</FormControl.Label>
                    <TextInput className={s.input} {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                control={methods.control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <FormControl
                    className={s.formInputWrapper}
                    required
                    layout="horizontal"
                  >
                    <FormControl.Label>Last Name</FormControl.Label>
                    <TextInput className={s.input} {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />

              {indexedPreferences.map((indexedPreference) => {
                const lookup =
                  preferenceInputs[indexedPreference.preference.key]
                if (!lookup || lookup.skip || lookup.footerSection) return null

                return (
                  <Controller
                    control={methods.control}
                    key={indexedPreference.index}
                    name={`preferences.${indexedPreference.index}`}
                    render={({ field, fieldState }) => (
                      <PrefrenceInputRender input={lookup} field={field} />
                    )}
                  ></Controller>
                )
              })}

              <div
                className={clsx(
                  s.customInput,
                  s.formInputWrapper,
                  s.align__top,
                )}
              >
                <div className={s.customInput__label}>Password</div>
                <div className={s.customInput__input}>
                  <a
                    onClick={() => {
                      actionOtpSendToken(customer?.email)
                      dispatch(
                        openModalView({
                          modalView: 'RESET_PASSWORD_MODAL',
                          props: {
                            data: {
                              email: customer?.email,
                              forgotPasswordText: labels?.forgotPasswordText,
                            },
                          },
                        }),
                      )
                    }}
                  >
                    Click to reset your password
                  </a>

                  <p className={s.info__text}>
                    An email will be sent to you with instructions on how to
                    reset your password.
                  </p>
                </div>
              </div>

              <div className={s.divider} />

              <UserProfileConsentNewsletterToggle />

              {indexedPreferences.map((indexedPreference) => {
                const lookup =
                  preferenceInputs[indexedPreference.preference.key]
                if (!lookup || lookup.skip || !lookup.footerSection) return null

                return (
                  <Controller
                    control={methods.control}
                    key={indexedPreference.index}
                    name={`preferences.${indexedPreference.index}`}
                    render={({ field }) => (
                      <PrefrenceInputRender input={lookup} field={field} />
                    )}
                  ></Controller>
                )
              })}

              <AccountTypeForm
                isLoading={loading}
                currentGroup={customerGroup}
              />

              <div className={s.divider} />

              <Button
                form={formId}
                className={s.button}
                size="small"
                loading={isPending || loading || loadingSpinner}
                type="submit"
              >
                Save changes
              </Button>

              <Button
                as="a"
                onClick={() =>
                  dispatch(
                    openModalView({
                      modalView: 'DELETE_ACCOUNT_MODAL',
                      props: { data: { id: customer?.id } },
                    }),
                  )
                }
                size="small"
                className={s.button}
                variant="buy"
              >
                Delete account
              </Button>
            </form>
          </FormProvider>
        </div>
      </div>

      <div className={s.section}>
        <h2 className={s.headline}>
          Addresses
          <a
            className={s.action}
            onClick={() =>
              dispatch(
                openModalView({
                  modalView: 'ADDRESS_MODAL',
                  props: {
                    data: {
                      customer: {
                        defaultShippingAddressId:
                          customer?.defaultShippingAddress?.id,
                        defaultBillingAddressId:
                          customer?.defaultBillingAddress?.id,
                      },
                    },
                  },
                }),
              )
            }
          >
            <HiPlus /> <span>New</span>
          </a>
        </h2>

        <div className={clsx(s.formGroup, s.addressGrid)}>
          {customer &&
            [...addresses]
              .sort((a, b) => {
                const defaultBillingAddressId =
                  customer.defaultBillingAddress?.id
                const defaultShippingAddressId =
                  customer.defaultShippingAddress?.id
                if (defaultBillingAddressId === a.id) return -1
                if (defaultShippingAddressId === b.id) return 1
                if (defaultShippingAddressId === a.id) return -1
                if (defaultShippingAddressId === b.id) return 1
                return 0
              })
              .map((address, index) => {
                return (
                  <Address
                    index={index}
                    address={address}
                    isDefaultShipping={
                      customer.defaultShippingAddress?.id === address.id
                    }
                    isDefaultBilling={
                      customer.defaultBillingAddress?.id === address.id
                    }
                  />
                )
              })}
        </div>
        {customer && addresses.length === 0 && (
          <p>You have no addresses saved to your account.</p>
        )}
      </div>
    </div>
  )
}

export default M61ProfileView
